import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { GUIDES } from "@/data/seoContent";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function Resources() {
  const jsonLd = [
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Guides", path: "/resources" }]),
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: GUIDES.map((g, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `/resources/${g.slug}`,
        name: g.title,
      })),
    },
  ];
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo
        title="HOA Guides & Playbooks, Communication, Compliance & Software | Ask Redline"
        description="Practical guides for HOAs and community associations: choosing management software, TCPA & A2P 10DLC texting compliance, reducing resident complaints and emergency notifications."
        canonical="/resources"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <BookOpen className="h-3.5 w-3.5" strokeWidth={2} /> Guides & playbooks
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">Run a sharper community.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            No-fluff guides for boards and management companies, how to choose software, stay compliant when you text residents,
            cut complaint volume and build an emergency system residents actually see.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-5">
        {GUIDES.map((g, i) => (
          <motion.div key={g.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}>
            <Link to={`/resources/${g.slug}`} data-testid={`guide-card-${g.slug}`}
              className="group flex flex-col h-full rounded-3xl border border-sand-200 bg-white p-8 hover:border-redline-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><g.icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-400">{g.category}</span>
              </div>
              <h2 className="font-heading text-xl font-extrabold tracking-tight group-hover:text-redline-600 transition-colors leading-snug">{g.title}</h2>
              <p className="text-sm text-ink-600 mt-3 leading-relaxed flex-1">{g.description}</p>
              <div className="mt-5 flex items-center justify-between text-xs text-ink-500">
                <span>{fmtDate(g.date)} · {g.readMins} min read</span>
                <span className="inline-flex items-center gap-1.5 font-semibold text-redline-600 group-hover:gap-2.5 transition-all">Read <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          </motion.div>
        ))}
      </section>

      <MarketingCTA />
      <MarketingFooter />
    </div>
  );
}
